import React, { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Sparkles,
  Eraser,
  ClipboardPaste,
  Camera,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import PuzzleCropModal from '../components/PuzzleCropModal';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Make an 81-char string out of the 9x9 grid (0 for empty).
function gridToString(g) {
  return g.flat().map((v) => (v === 0 ? '0' : String(v))).join('');
}

// Inverse: parse an 81-char (or whitespace-tolerant) string into a 9x9 grid.
function stringToGrid(s) {
  const cleaned = [...s].filter((c) => /\d|\./.test(c)).map((c) => (c === '.' ? '0' : c));
  if (cleaned.length !== 81) return null;
  const out = [];
  for (let r = 0; r < 9; r++) out.push(cleaned.slice(r * 9, r * 9 + 9).map((c) => parseInt(c, 10)));
  return out;
}

const EMPTY = () => Array.from({ length: 9 }, () => Array(9).fill(0));

// Verdict → visual treatment. Centralized so the result card reads
// consistently and color/icon match the user's mental model.
const VERDICT_STYLES = {
  bonafide:           { color: '#34C759', icon: CheckCircle2,   label: 'Bonafide' },
  needs_search:       { color: '#FF9500', icon: AlertTriangle,  label: 'Needs Search' },
  multiple_solutions: { color: '#FF3B30', icon: XCircle,        label: 'Multiple Solutions' },
  no_solution:        { color: '#FF3B30', icon: XCircle,        label: 'No Solution' },
  malformed:          { color: '#FF3B30', icon: XCircle,        label: 'Malformed' },
};

export default function ValidatorPage() {
  const { user } = useAuth();
  const [grid, setGrid] = useState(EMPTY);
  const [activeCell, setActiveCell] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  // Crop flow: pick photo → open crop modal → crop → upload
  const [pendingCropFile, setPendingCropFile] = useState(null);

  const cluesGiven = useMemo(
    () => grid.flat().filter((v) => v !== 0).length,
    [grid]
  );

  const setCell = (r, c, v) => {
    setGrid((prev) => {
      const next = prev.map((row) => row.slice());
      next[r][c] = v;
      return next;
    });
  };

  const handleKey = useCallback(
    (e) => {
      if (!activeCell) return;
      const { r, c } = activeCell;
      if (e.key >= '1' && e.key <= '9') {
        setCell(r, c, parseInt(e.key, 10));
        // Auto-advance to next cell so typing a row feels natural.
        const next = c < 8 ? { r, c: c + 1 } : r < 8 ? { r: r + 1, c: 0 } : null;
        if (next) setActiveCell(next);
      } else if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') {
        setCell(r, c, 0);
      } else if (e.key === 'ArrowLeft' && c > 0) setActiveCell({ r, c: c - 1 });
      else if (e.key === 'ArrowRight' && c < 8) setActiveCell({ r, c: c + 1 });
      else if (e.key === 'ArrowUp' && r > 0) setActiveCell({ r: r - 1, c });
      else if (e.key === 'ArrowDown' && r < 8) setActiveCell({ r: r + 1, c });
    },
    [activeCell]
  );

  React.useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const clear = () => {
    setGrid(EMPTY());
    setResult(null);
    setActiveCell(null);
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = stringToGrid(text);
      if (!parsed) {
        toast.error('Clipboard does not contain 81 digits.');
        return;
      }
      setGrid(parsed);
      setResult(null);
      toast.success('Pasted from clipboard.');
    } catch {
      toast.error('Clipboard access denied — paste manually.');
    }
  };

  // OCR: snap or upload a photo of a printed puzzle, gpt-4o vision
  // extracts the 81-digit grid and loads it into the editor for review.
  // Requires authentication (server enforces rate limits per tier).
  const handlePhoto = (file) => {
    if (!file) return;
    if (!user) {
      toast.info('Sign in to use photo OCR');
      return;
    }
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error('Use a JPEG, PNG, or WEBP image.');
      return;
    }
    setPendingCropFile(file);
  };

  // Called when the crop modal confirms — uploads cropped Blob to OCR.
  const handleCroppedBlob = async (blob) => {
    setPendingCropFile(null);
    if (!blob) return;
    setOcrBusy(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result || '';
          resolve(String(result).split(',')[1] || '');
        };
        reader.onerror = () => reject(new Error('file-read-failed'));
        reader.readAsDataURL(blob);
      });
      const { data } = await axios.post(
        `${API_URL}/api/puzzle-ocr`,
        { image_base64: base64, mime_type: 'image/jpeg' },
        { withCredentials: true },
      );
      if (!data.ok || !data.grid) {
        toast.error(data.message || 'Could not read a sudoku grid from that photo. Try a tighter crop on just the 9x9 grid.');
        return;
      }
      const parsed = stringToGrid(data.grid);
      if (!parsed) {
        toast.error('OCR returned an unparseable grid.');
        return;
      }
      setGrid(parsed);
      setResult(null);
      setActiveCell({ r: 0, c: 0 });
      const conf = data.confidence === 'high' ? 'High' : data.confidence === 'medium' ? 'Medium' : 'Low';
      const quotaMsg = data.quota_limit > 0 ? ` (${data.quota_used}/${data.quota_limit} today)` : '';
      toast.success(`${conf} confidence — please proofread before validating${quotaMsg}.`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Failed to process image';
      toast.error(detail);
    } finally {
      setOcrBusy(false);
    }
  };

  const validate = async () => {
    if (cluesGiven < 17) {
      toast.error('A sudoku requires at least 17 clues for a unique solution.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/validator`, {
        grid: gridToString(grid),
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const verdictStyle = result ? VERDICT_STYLES[result.verdict] || VERDICT_STYLES.malformed : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#34C759]/15 text-[#34C759] text-xs font-mono mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Validator
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2">
            Is your puzzle bonafide?
          </h1>
          <p className="text-[var(--text-secondary)] max-w-2xl mx-auto">
            Paste or type any sudoku grid and we'll check if it's a real
            deductive puzzle — exactly one solution, fully solvable by pure
            logic, no trial-and-error needed.
          </p>
        </div>

        <div className="grid lg:grid-cols-[auto_1fr] gap-6 items-start">
          {/* Grid editor */}
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
            <CardContent className="p-4 sm:p-6">
              <div
                className="grid grid-cols-9 gap-0 bg-[var(--border-strong)] p-px rounded mx-auto"
                style={{ width: 'min(100%, 405px)' }}
              >
                {grid.map((row, r) =>
                  row.map((val, c) => {
                    const isActive = activeCell && activeCell.r === r && activeCell.c === c;
                    const heavyRight = c % 3 === 2 && c !== 8;
                    const heavyBottom = r % 3 === 2 && r !== 8;
                    return (
                      <button
                        key={`${r}-${c}`}
                        onClick={() => setActiveCell({ r, c })}
                        className={`aspect-square flex items-center justify-center font-mono text-xl font-semibold transition-colors ${
                          isActive
                            ? 'bg-[#8B5A3C]/25 text-[var(--text-primary)]'
                            : val
                            ? 'bg-[var(--background-elevated)] text-[var(--text-primary)]'
                            : 'bg-[var(--background-surface)] text-[var(--text-disabled)] hover:bg-[var(--hover-bg-subtle)]'
                        }`}
                        style={{
                          marginRight: heavyRight ? '1px' : 0,
                          marginBottom: heavyBottom ? '1px' : 0,
                        }}
                        data-testid={`validator-cell-${r}-${c}`}
                      >
                        {val === 0 ? '' : val}
                      </button>
                    );
                  })
                )}
              </div>
              {/* On-screen numpad for mobile: 9 digit keys + a clearly-separated Clear button */}
              <div className="grid grid-cols-9 gap-1 mt-4">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button
                    key={n}
                    onClick={() => {
                      if (!activeCell) return;
                      setCell(activeCell.r, activeCell.c, n);
                      const next =
                        activeCell.c < 8
                          ? { r: activeCell.r, c: activeCell.c + 1 }
                          : activeCell.r < 8
                          ? { r: activeCell.r + 1, c: 0 }
                          : null;
                      if (next) setActiveCell(next);
                    }}
                    className="py-2 rounded bg-[var(--background-elevated)] hover:bg-[var(--hover-bg)] text-[var(--text-primary)] font-mono text-base"
                    data-testid={`validator-num-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  if (!activeCell) return;
                  setCell(activeCell.r, activeCell.c, 0);
                }}
                className="w-full mt-2 py-2 rounded bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 font-medium text-sm flex items-center justify-center gap-2"
                data-testid="validator-num-0"
              >
                <Eraser className="w-4 h-4" /> Clear cell
              </button>
              <div className="flex items-center justify-between mt-4 text-xs text-[var(--text-disabled)]">
                <span>{cluesGiven} clues given</span>
                <span>Click a cell, type a digit. Backspace clears.</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clear}
                  className="flex-1"
                  data-testid="validator-clear"
                >
                  <Eraser className="w-4 h-4 mr-2" /> Clear
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={pasteFromClipboard}
                  className="flex-1"
                  data-testid="validator-paste"
                >
                  <ClipboardPaste className="w-4 h-4 mr-2" /> Paste
                </Button>
              </div>
              {/* Photo OCR — gpt-4o vision via /api/puzzle-ocr */}
              {user ? (
                <label
                  className={`mt-2 inline-flex items-center justify-center gap-2 w-full py-2 rounded text-sm font-medium cursor-pointer transition-colors ${
                    ocrBusy
                      ? 'bg-[var(--hover-bg-subtle)] text-[var(--text-disabled)] border border-[var(--border-subtle)] cursor-wait'
                      : 'bg-[#8B5A3C]/15 text-[#A46944] border border-[#8B5A3C]/30 hover:bg-[#8B5A3C]/25'
                  }`}
                  data-testid="validator-photo-label"
                >
                  {ocrBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  <span>{ocrBusy ? 'Reading photo…' : 'Snap or upload a photo'}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    disabled={ocrBusy}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      handlePhoto(file);
                      e.target.value = '';
                    }}
                    data-testid="validator-photo-input"
                  />
                </label>
              ) : (
                <Link
                  to="/login"
                  className="mt-2 inline-flex items-center justify-center gap-2 w-full py-2 rounded text-sm font-medium bg-[var(--hover-bg-subtle)] text-[var(--text-secondary)] border border-[var(--border-subtle)] hover:text-[var(--text-primary)]"
                  data-testid="validator-photo-signin"
                >
                  <Camera className="w-4 h-4" />
                  <span>Sign in to scan a photo</span>
                </Link>
              )}
              <Button
                onClick={validate}
                disabled={loading || cluesGiven < 17}
                className="w-full mt-3 bg-[#34C759] hover:bg-[#3FD06F] text-white"
                data-testid="validator-run"
              >
                {loading ? 'Checking…' : 'Check this puzzle'}
              </Button>
            </CardContent>
          </Card>

          {/* Result panel */}
          <div>
            {!result && (
              <Card className="bg-[var(--background-surface)] border-dashed border-[var(--border-subtle)] h-full">
                <CardContent className="p-8 text-center text-[var(--text-secondary)] flex flex-col justify-center min-h-[300px]">
                  <Sparkles className="w-10 h-10 mx-auto mb-3 text-[var(--text-disabled)]" />
                  <p>
                    Enter a grid and hit <strong>Check this puzzle</strong> to
                    run uniqueness, deducibility, and clue-balance checks.
                  </p>
                </CardContent>
              </Card>
            )}

            {result && verdictStyle && (
              <Card
                className="bg-[var(--background-surface)]"
                style={{ borderColor: verdictStyle.color }}
                data-testid="validator-result"
              >
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <verdictStyle.icon className="w-7 h-7 flex-shrink-0" style={{ color: verdictStyle.color }} />
                    <div>
                      <Badge style={{ backgroundColor: verdictStyle.color, color: 'white' }}>
                        {verdictStyle.label}
                      </Badge>
                      {typeof result.suggested_level === 'number' && result.suggested_level > 0 && (
                        <span className="ml-2 text-xs text-[var(--text-disabled)]">
                          Level {result.suggested_level} · {result.suggested_level_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-[var(--text-primary)] leading-relaxed">{result.message}</p>

                  {result.parse_ok && (
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[var(--border-subtle)]">
                      <div>
                        <div className="text-xs text-[var(--text-disabled)] mb-0.5">Clues</div>
                        <div className="font-mono text-lg text-[var(--text-primary)]">
                          {result.cells_given}
                        </div>
                      </div>
                      {result.hardest_technique && (
                        <div>
                          <div className="text-xs text-[var(--text-disabled)] mb-0.5">Hardest</div>
                          <div className="text-sm text-[var(--text-primary)]">
                            {result.hardest_technique}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {result.candidate_balance && result.candidate_balance.underrepresented?.length > 0 && (
                    <div className="pt-2 border-t border-[var(--border-subtle)]">
                      <div className="text-xs text-[var(--text-disabled)] mb-1">Clue balance</div>
                      <p className="text-sm text-[#FF9500]">
                        Digits with fewer than 2 clues:{' '}
                        <strong>
                          {result.candidate_balance.underrepresented.join(', ')}
                        </strong>{' '}
                        — these often cause solvers to stall.
                      </p>
                    </div>
                  )}

                  {result.technique_chain && result.technique_chain.length > 0 && (
                    <div className="pt-2 border-t border-[var(--border-subtle)]">
                      <div className="text-xs text-[var(--text-disabled)] mb-2">
                        Technique chain ({result.technique_chain.length} steps)
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {result.technique_chain.map((t, i) => (
                          <Badge key={`${t}-${i}`} variant="secondary" className="text-xs">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      {pendingCropFile && (
        <PuzzleCropModal
          imageFile={pendingCropFile}
          onCancel={() => setPendingCropFile(null)}
          onConfirm={handleCroppedBlob}
        />
      )}
    </div>
  );
}
